// Web localStorage equivalent of mobile AsyncStorage utilities

// Quiz scores
export const saveQuizScore = (quizType, score, total) => {
  try {
    const existing = localStorage.getItem('quizScores');
    const scores = existing ? JSON.parse(existing) : [];
    scores.push({
      quizType,
      score,
      total,
      percentage: Math.round((score / total) * 100),
      date: new Date().toISOString(),
    });
    // Keep last 50 scores
    const trimmed = scores.slice(-50);
    localStorage.setItem('quizScores', JSON.stringify(trimmed));
  } catch (error) {
    console.error('Error saving quiz score:', error);
  }
};

export const getQuizScores = () => {
  try {
    const scores = localStorage.getItem('quizScores');
    return scores ? JSON.parse(scores) : [];
  } catch (error) {
    console.error('Error getting quiz scores:', error);
    return [];
  }
};

// Learned words tracking
export const saveLearnedWord = (wordId, question, answer, section, translations = {}) => {
  try {
    const existing = localStorage.getItem('learnedWordsDetail');
    const words = existing ? JSON.parse(existing) : [];
    const key = `${section || 'unknown'}_${wordId}`;
    if (!words.find(w => w.key === key)) {
      words.push({
        key,
        id: wordId,
        question: question || '',
        answer: answer || '',
        ...translations,
        section: section || 'Unknown',
        learnedAt: new Date().toISOString(),
      });
      localStorage.setItem('learnedWordsDetail', JSON.stringify(words));
    }
    // Keep legacy list in sync for backward compat
    const legacyWords = localStorage.getItem('learnedWords');
    const legacyList = legacyWords ? JSON.parse(legacyWords) : [];
    if (!legacyList.includes(wordId)) {
      legacyList.push(wordId);
      localStorage.setItem('learnedWords', JSON.stringify(legacyList));
    }
  } catch (error) {
    console.error('Error saving learned word:', error);
  }
};

export const getLearnedWords = () => {
  try {
    const words = localStorage.getItem('learnedWords');
    return words ? JSON.parse(words) : [];
  } catch (error) {
    console.error('Error getting learned words:', error);
    return [];
  }
};

export const getLearnedWordsDetail = () => {
  try {
    const words = localStorage.getItem('learnedWordsDetail');
    return words ? JSON.parse(words) : [];
  } catch (error) {
    console.error('Error getting learned words detail:', error);
    return [];
  }
};

// Study streak tracking
export const updateStudyStreak = () => {
  try {
    const data = localStorage.getItem('studyStreak');
    const streak = data ? JSON.parse(data) : { count: 0, lastDate: null };
    const today = new Date().toISOString().split('T')[0];

    if (streak.lastDate === today) {
      return streak;
    }

    const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];
    if (streak.lastDate === yesterday) {
      streak.count += 1;
    } else {
      streak.count = 1;
    }
    streak.lastDate = today;
    localStorage.setItem('studyStreak', JSON.stringify(streak));
    return streak;
  } catch (error) {
    console.error('Error updating study streak:', error);
    return { count: 0, lastDate: null };
  }
};

export const getStudyStreak = () => {
  try {
    const data = localStorage.getItem('studyStreak');
    return data ? JSON.parse(data) : { count: 0, lastDate: null };
  } catch (error) {
    console.error('Error getting study streak:', error);
    return { count: 0, lastDate: null };
  }
};

// Bookmarked words
export const toggleBookmark = (wordId) => {
  try {
    const existing = localStorage.getItem('bookmarkedWords');
    const words = existing ? JSON.parse(existing) : [];
    const index = words.indexOf(wordId);
    if (index === -1) {
      words.push(wordId);
    } else {
      words.splice(index, 1);
    }
    localStorage.setItem('bookmarkedWords', JSON.stringify(words));
    return words;
  } catch (error) {
    console.error('Error toggling bookmark:', error);
    return [];
  }
};

export const getBookmarkedWords = () => {
  try {
    const words = localStorage.getItem('bookmarkedWords');
    return words ? JSON.parse(words) : [];
  } catch (error) {
    console.error('Error getting bookmarked words:', error);
    return [];
  }
};

// Failed questions
export const getStoredFailedQuestions = () => {
  try {
    const questions = localStorage.getItem('failedQuestions');
    return questions ? JSON.parse(questions) : [];
  } catch (error) {
    console.error('Error getting failed questions:', error);
    return [];
  }
};

// Onboarding
export const hasSeenOnboarding = () => {
  try {
    return localStorage.getItem('hasSeenOnboarding') === 'true';
  } catch (error) {
    return false;
  }
};

export const setOnboardingComplete = () => {
  try {
    localStorage.setItem('hasSeenOnboarding', 'true');
  } catch (error) {
    console.error('Error saving onboarding state:', error);
  }
};

// Theme
export const saveDarkMode = (isDark) => {
  try {
    localStorage.setItem('darkMode', isDark ? 'true' : 'false');
  } catch (error) {
    console.error('Error saving dark mode:', error);
  }
};

export const getDarkMode = () => {
  try {
    return localStorage.getItem('darkMode') === 'true';
  } catch (error) {
    return false;
  }
};

// Language preference
export const saveUserLanguage = (language) => {
  try {
    localStorage.setItem('userLanguage', language);
  } catch (error) {
    console.error('Error saving user language:', error);
  }
};

export const getUserLanguage = () => {
  try {
    return localStorage.getItem('userLanguage') || 'en';
  } catch (error) {
    return 'en';
  }
};
